import { useEffect, useMemo, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { Check, Eye, History, LayoutTemplate, Loader2, RotateCcw, Save, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  usePoLogo,
  usePoTemplate,
  usePoTemplateVersion,
  usePoTemplateVersions,
  useResetPoTemplate,
  useSavePoTemplate,
} from '@/hooks/usePurchaseOrders'
import { customFieldsProblem, layoutChanged } from '@/lib/poTemplate/edit'
import { samplePurchaseOrder } from '@/lib/poTemplate/sample'
import { formatPoDate } from '@/components/inventory/purchase-orders/poStatus'
import { PO_PAPER, PoDocument } from '@/components/inventory/purchase-orders/document/PoDocument'
import { PoScaledPreview } from '@/components/inventory/purchase-orders/document/PoScaledPreview'
import type { PoTemplateLayout } from '@/types/purchaseOrders'
import type { PoTemplatePreset } from '@/lib/poTemplate/presets'
import { PoTemplateEditorPanels } from './PoTemplateEditorPanels'
import { PoTemplateGallery } from './PoTemplateGallery'

/**
 * Configure → Purchase Orders (z8nrz7f7dz): the tenant's PO template.
 *
 * Edits on the left, the page they produce on the right — the same
 * PoDocument Print and Download PDF use, filled with the sample order. Every
 * save is a new version: sent orders keep the snapshot they went out with, so
 * changing the template never rewrites an order already with a supplier.
 */
export function PoTemplateSettings() {
  const current = usePoTemplate()
  const versions = usePoTemplateVersions()
  const save = useSavePoTemplate()
  const reset = useResetPoTemplate()

  const [draft, setDraft] = useState<PoTemplateLayout | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [savedFlash, setSavedFlash] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [viewing, setViewing] = useState<number | null>(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  // The last template applied from the gallery and the draft it replaced, for Undo.
  const [applied, setApplied] = useState<{ name: string; previous: PoTemplateLayout } | null>(null)
  const viewed = usePoTemplateVersion(viewing)

  // Seeded from the server once; a refetch must not replace an edit in progress.
  useEffect(() => {
    if (!draft && current.data) setDraft(current.data.layout)
  }, [draft, current.data])

  const dirty = !!draft && layoutChanged(draft, current.data?.layout)
  const shown = viewing !== null ? viewed.data?.layout : draft
  const logoUrl = usePoLogo(shown?.branding.logoAssetKey)
  const sample = useMemo(() => (shown ? samplePurchaseOrder(shown) : null), [shown])

  // Leaving with unsaved edits — another route, another Configure tab, or the
  // browser itself — asks first.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  )
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function doSave() {
    if (!draft) return
    // Dropdown options are typed comma-separated; a stray comma leaves a blank
    // entry, dropped here rather than while typing (which would eat the comma).
    const layout = {
      ...draft,
      customFields: draft.customFields.map((f) =>
        f.options ? { ...f, options: f.options.map((o) => o.trim()).filter(Boolean) } : f,
      ),
    }
    const problem = customFieldsProblem(layout.customFields)
    if (problem) return setError(problem)
    setError(null)
    save.mutate(
      { layout, note: note.trim() || undefined },
      {
        onSuccess: (t) => {
          setDraft(t.layout)
          setApplied(null)
          setNote('')
          setSavedFlash(true)
          window.setTimeout(() => setSavedFlash(false), 2500)
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'The template could not be saved.'),
      },
    )
  }

  // A template from the gallery goes into the draft only; Save makes it a version.
  function applyPreset(preset: PoTemplatePreset) {
    if (!draft) return
    setApplied({ name: preset.name, previous: draft })
    setDraft(preset.build(draft))
    setGalleryOpen(false)
    setError(null)
  }

  function undoApply() {
    if (!applied) return
    setDraft(applied.previous)
    setApplied(null)
  }

  function doReset() {
    reset.mutate(undefined, {
      onSuccess: (t) => {
        setDraft(t.layout)
        setApplied(null)
        setConfirmReset(false)
      },
      onError: (e) => setError(e instanceof Error ? e.message : 'The template could not be reset.'),
    })
  }

  if (current.isLoading || !draft) {
    return (
      <div className='flex items-center gap-2 py-12 text-sm text-muted-foreground'>
        {current.error ? (
          <span className='text-destructive'>
            {current.error instanceof Error ? current.error.message : 'Could not load the PO template.'}
          </span>
        ) : (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading the PO template…
          </>
        )}
      </div>
    )
  }

  const readOnly = viewing !== null
  const paper = shown?.document.paperSize ?? 'A4'

  return (
    <div className='space-y-4'>
      <header className='flex flex-wrap items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm'>
        <div className='min-w-0'>
          <h2 className='text-base font-semibold text-slate-900'>Purchase order template</h2>
          <p className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500'>
            <span className='font-medium text-slate-700'>
              {current.data?.isDefault ? 'Built-in default' : `Version ${current.data?.version}`}
            </span>
            <span aria-hidden className='text-slate-300'>·</span>
            {dirty ? (
              <span className='inline-flex items-center gap-1.5 font-medium text-amber-700'>
                <span className='h-1.5 w-1.5 rounded-full bg-amber-500' aria-hidden />
                Unsaved changes
              </span>
            ) : savedFlash ? (
              <span className='inline-flex items-center gap-1 font-medium text-emerald-700'>
                <Check className='h-3.5 w-3.5' />
                Saved
              </span>
            ) : (
              <span>All changes saved</span>
            )}
          </p>
          <p className='mt-1 text-xs text-slate-500'>
            What your suppliers receive. Saving creates a new version; orders already sent keep the one they went out with.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Input
            className='h-9 w-56'
            placeholder='Note for this version (optional)'
            aria-label='Note for this version'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={readOnly}
          />
          <Button size='sm' variant='outline' className='gap-1.5' onClick={() => setGalleryOpen(true)} disabled={readOnly}>
            <LayoutTemplate className='h-4 w-4' />
            Browse templates
          </Button>
          <Button size='sm' variant='outline' className='gap-1.5' onClick={() => setConfirmReset(true)} disabled={readOnly}>
            <RotateCcw className='h-4 w-4' />
            Reset to default
          </Button>
          <Button size='sm' className='gap-1.5' onClick={doSave} disabled={!dirty || save.isPending || readOnly}>
            {save.isPending ? <Loader2 className='h-4 w-4 animate-spin' /> : <Save className='h-4 w-4' />}
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>
      {error && (
        <p role='alert' className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-destructive'>
          {error}
        </p>
      )}

      {!readOnly && current.data?.isDefault && !dirty && !applied && (
        <div className='flex flex-wrap items-center gap-4 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-5 py-4'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm'>
            <LayoutTemplate className='h-5 w-5' />
          </div>
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-slate-900'>Start from a standard template</p>
            <p className='text-xs text-slate-600'>
              US commercial, EU VAT, life sciences / GMP and more — previewed with your logo. Pick one, adjust it, and save.
            </p>
          </div>
          <Button size='sm' className='gap-1.5' onClick={() => setGalleryOpen(true)}>
            <LayoutTemplate className='h-4 w-4' />
            Browse templates
          </Button>
        </div>
      )}

      {!readOnly && applied && (
        <div
          role='status'
          className='flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900'
        >
          <span>
            Applied <span className='font-semibold'>{applied.name}</span> — review and Save to make it your template.
          </span>
          <span className='flex items-center gap-3'>
            <button type='button' className='inline-flex items-center gap-1 font-medium underline' onClick={undoApply}>
              <Undo2 className='h-3.5 w-3.5' />
              Undo
            </button>
            <button type='button' className='text-sky-700 hover:underline' onClick={() => setApplied(null)}>
              Dismiss
            </button>
          </span>
        </div>
      )}

      {readOnly && (
        <div className='flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          <span>Viewing version {viewing}, read-only.</span>
          <button type='button' className='font-medium underline' onClick={() => setViewing(null)}>
            Back to editing
          </button>
        </div>
      )}

      <div className='grid gap-6 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]'>
        <div className='min-w-0 space-y-4'>
          {!readOnly ? (
            <PoTemplateEditorPanels layout={draft} onChange={setDraft} />
          ) : viewed.data ? (
            <PoTemplateEditorPanels layout={viewed.data.layout} onChange={() => undefined} readOnly />
          ) : (
            <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
          )}

          <section className='rounded-xl border border-slate-200 bg-white shadow-sm'>
            <header className='flex items-center gap-2 border-b border-slate-100 px-5 py-3'>
              <History className='h-4 w-4 text-slate-400' />
              <h3 className='text-sm font-semibold text-slate-900'>Version history</h3>
            </header>
            {(versions.data ?? []).length === 0 ? (
              <p className='px-5 py-4 text-sm text-slate-500'>No saved versions yet.</p>
            ) : (
              <ul className='divide-y divide-slate-100 text-sm'>
                {[...(versions.data ?? [])]
                  .sort((a, b) => b.version - a.version)
                  .map((v) => (
                    <li
                      key={v.version}
                      className={cn('flex flex-wrap items-center gap-x-3 px-5 py-2', viewing === v.version && 'bg-amber-50/60')}
                    >
                      <span className='font-medium text-slate-900'>v{v.version}</span>
                      {v.version === current.data?.version && (
                        <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'>current</span>
                      )}
                      <span className='min-w-0 truncate text-slate-600'>{v.note ?? ''}</span>
                      <span className='ml-auto text-xs text-slate-500'>
                        {formatPoDate(v.createdAt)}
                        {v.createdBy ? ` · ${v.createdBy}` : ''}
                      </span>
                      <button
                        type='button'
                        className='text-xs font-medium text-primary hover:underline'
                        onClick={() => setViewing(viewing === v.version ? null : v.version)}
                      >
                        {viewing === v.version ? 'Close' : 'View'}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </section>
        </div>

        <div className='min-w-0'>
          <div className='overflow-hidden rounded-xl border border-slate-200 bg-slate-100 xl:sticky xl:top-4'>
            <div className='flex items-center justify-between gap-2 border-b border-slate-200 bg-white/70 px-4 py-2.5'>
              <p className='flex items-center gap-1.5 text-xs font-medium text-slate-600'>
                <Eye className='h-3.5 w-3.5 text-slate-400' />
                Live preview <span className='text-slate-400'>· sample order</span>
              </p>
              <span className='rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-600'>
                {paper}
              </span>
            </div>
            <div className='max-h-[calc(100vh-10rem)] overflow-auto p-4'>
              {shown && sample ? (
                <PoScaledPreview paperWidthMm={(PO_PAPER[shown.document.paperSize] ?? PO_PAPER.A4).widthMm}>
                  <PoDocument layout={shown} po={sample} logoUrl={logoUrl} mode='preview' />
                </PoScaledPreview>
              ) : (
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              )}
            </div>
          </div>
        </div>
      </div>

      {!readOnly && (
        <PoTemplateGallery
          open={galleryOpen}
          onOpenChange={setGalleryOpen}
          base={draft}
          logoUrl={logoUrl}
          onApply={applyPreset}
        />
      )}

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Reset to the default template?</DialogTitle>
            <DialogDescription>
              This saves the built-in default as a new version. Your current template stays in the version
              history, and orders already sent keep the template they went out with.
              {dirty && ' Unsaved changes here are discarded.'}
            </DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={doReset} disabled={reset.isPending}>
              {reset.isPending ? 'Resetting…' : 'Reset to default'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={blocker.state === 'blocked'} onOpenChange={(o) => !o && blocker.reset?.()}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Leave without saving?</DialogTitle>
            <DialogDescription>Your changes to the PO template have not been saved.</DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => blocker.reset?.()}>
              Stay
            </Button>
            <Button variant='destructive' onClick={() => blocker.proceed?.()}>
              Discard changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
